import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import {
  ErrorMessageResponse,
  PickupSlot,
  PreferredTiming,
  ReslotSummary,
  SlotResponseResult,
  TimeSlotEditResult,
} from '@/types';
import useLanguageStore from '@/stores/useLanguageStore';
import {
  fetchPickupSlots,
  respondPickupSlot,
  updatePickupTimeSlot,
  updateSlotPreference,
} from '@/api';

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const PICKUP_SLOTS_QUERY_KEY = ['pickup-slots'];
const PICKUPS_QUERY_KEY = ['pickups'];

// Fetch the merchant's pickup time-slot options.
//
// v2-task-8b (audit defects: banner label stuck until reload / SlotModal
// FOUC): `slot.label` is localized SERVER-SIDE. The request's locale signal
// is the `lang` param set by the `setLangAsParams` axios interceptor
// (src/config/api.config.ts), read directly from
// `useLanguageStore.getState().language` — NOT from `i18n.language` (that
// only drives the app's `t()` strings and can briefly disagree with the
// store on mount: i18next initializes synchronously to DEFAULT_LANGUAGE
// while the persisted language is only pushed into it via a `useEffect` in
// src/index.tsx). The query key used to be locale-agnostic, so:
//   1. Toggling the language re-rendered every `t()` string via
//      react-i18next, but the already-cached slot payload — fetched under
//      whatever `language` was active at the time — was never invalidated,
//      so CurrentSlotBanner's slot label stayed in the old language until a
//      hard reload.
//   2. On first mount there's a brief window where the rendered `t()`
//      strings and this cached fetch can be one render behind the settled
//      `language` value, producing the ~1s "English before Arabic
//      resolves" SlotModal flash the audit reported.
// Scoping the query key by the SAME `language` value the interceptor reads
// fixes both: any change is a brand-new cache key, so React Query treats it
// as a fresh (isLoading=true) fetch — the existing `isLoading` Spin gate in
// SlotModal/CurrentSlotBanner naturally covers the brief in-flight moment
// instead of showing stale-locale text indefinitely.
// GET /merchants/pick-up-slots `object` is the bare slots ARRAY (a
// backward-compatible contract — see PickupSlotsResponse). The hook normalizes
// it to `{ slots }` so consumers keep reading `data?.slots`. The merchant's
// SAVED preferred timing is NOT on this feed — read it off GET /user
// (setting.pickup_preferred_timing) where the slot code already lives.
export interface PickupSlotsData {
  slots: PickupSlot[];
}

export const useFetchPickupSlots = (enabled = true) => {
  const language = useLanguageStore((state) => state.language);
  return useSentryQuery<PickupSlotsData, ErrorMessageResponse>(
    [...PICKUP_SLOTS_QUERY_KEY, language],
    async () => {
      const response = await fetchPickupSlots();
      return {
        slots: response.object ?? [],
      };
    },
    {
      enabled,
      staleTime: FIVE_MINUTES_MS,
    },
  );
};

// Update the merchant's preferred pickup time slot.
//
// v2-task-9: the mutation variable shape grew from a bare slot-code string
// to `{ timeSlotCode, autoSelectNextSlot? }` so SlotModal's Confirm button
// (preference mode) can submit the slot choice AND the auto-select toggle
// in one call — see pickupSlotService.ts::updateSlotPreference for the
// pinned request-body contract this maps onto.
//
// v2-task-10fe: grew a third optional field, `recurringPickupEnabled`, so
// CurrentSlotBanner's Confirm can submit the slot choice, auto-select, AND
// the recurring-pickup toggle together in one call.
//
// v1.1: grew a fourth optional field, `preferredTiming` (null = Anytime), so
// the preference Confirm can submit the slot choice AND the merchant's
// requested timing bucket together in one call.
export interface UpdateSlotPreferenceVariables {
  timeSlotCode: string;
  autoSelectNextSlot?: boolean;
  recurringPickupEnabled?: boolean;
  preferredTiming?: PreferredTiming | null;
}

export const useUpdateSlotPreference = () => {
  const queryClient = useQueryClient();

  // Slot-change consolidation: resolves with `reslotted {moved, skipped}` —
  // how many open same-day pickups followed the merchant onto the new slot,
  // and how many were route-locked and stayed put — so callers can toast the
  // actual outcome.
  return useSentryMutation<ReslotSummary, ErrorMessageResponse, UpdateSlotPreferenceVariables>(
    ({ timeSlotCode, autoSelectNextSlot, recurringPickupEnabled, preferredTiming }) =>
      updateSlotPreference(
        timeSlotCode,
        autoSelectNextSlot,
        recurringPickupEnabled,
        preferredTiming,
      ),
    {
      defaultErrorMessage: t('operation_failed_please_try_again'),
      onSuccess: () => {
        queryClient.invalidateQueries(PICKUPS_QUERY_KEY);
        queryClient.invalidateQueries(PICKUP_SLOTS_QUERY_KEY);
      },
    },
  );
};

// Update a single pickup's time slot.
//
// Resolves with the edit result (`relocated_from_route` etc.) so the caller can
// warn the merchant when this edit moved an already-routed pickup onto the new
// slot. onSuccess invalidates the pickups + pickup-slots caches so the moved
// slot refreshes on the list / Shipment Task instead of showing the stale slot.
export const useUpdatePickupTimeSlot = () => {
  const queryClient = useQueryClient();

  return useSentryMutation<
    TimeSlotEditResult,
    ErrorMessageResponse,
    { uid: string; timeSlotCode: string; preferredTiming?: PreferredTiming | null }
  >(
    ({ uid, timeSlotCode, preferredTiming }) =>
      updatePickupTimeSlot(uid, timeSlotCode, preferredTiming),
    {
      defaultErrorMessage: t('operation_failed_please_try_again'),
      onSuccess: () => {
        queryClient.invalidateQueries(PICKUPS_QUERY_KEY);
        queryClient.invalidateQueries(PICKUP_SLOTS_QUERY_KEY);
      },
    },
  );
};

// Respond to a pickup's slot-confirmation prompt (accept, change, or defer)
export const useRespondPickupSlot = () => {
  const queryClient = useQueryClient();

  return useSentryMutation<
    SlotResponseResult,
    ErrorMessageResponse,
    {
      uid: string;
      choice: 'accept' | 'change' | 'defer';
      timeSlotCode?: string;
      preferredTiming?: PreferredTiming | null;
    }
  >(
    ({ uid, choice, timeSlotCode, preferredTiming }) =>
      respondPickupSlot(uid, choice, timeSlotCode, preferredTiming),
    {
      defaultErrorMessage: t('operation_failed_please_try_again'),
      onSuccess: () => {
        queryClient.invalidateQueries(PICKUPS_QUERY_KEY);
        queryClient.invalidateQueries(PICKUP_SLOTS_QUERY_KEY);
      },
    },
  );
};
